import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lightbulb, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Bug fix v1.4.1: Decode HTML entities from backend sanitization
const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

// Clean text for social media copy - strip any markdown and keep plain text
const cleanForSocialMedia = (str) => {
  if (!str || typeof str !== 'string') return str;
  return decodeHtmlEntities(str)
    // Remove markdown bold **text** or __text__
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    // Remove markdown italic *text* or _text_
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '$1')
    .replace(/(?<!_)_([^_]+)_(?!_)/g, '$1')
    // Remove markdown headers
    .replace(/^#{1,6}\s+/gm, '')
    // Remove markdown list markers
    .replace(/^[-*]\s+/gm, '')
    // Keep line breaks and emojis (important for social media!)
    .trim();
};

export const RewriteSuggestion = ({ suggestion }) => {
  const [copied, setCopied] = useState(false);

  if (!suggestion) return null;

  const handleCopy = async () => {
    try {
      // Clean for social media: remove markdown, keep line breaks and emojis
      const cleanText = cleanForSocialMedia(suggestion);
      await navigator.clipboard.writeText(cleanText);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  return (
    <Card className="border border-border shadow-md bg-accent/50">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-warning" />
            Suggested Opening
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="h-8 px-3 text-xs font-medium hover:bg-background"
            aria-label="Copy suggestion to clipboard"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 mr-1.5 text-success" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                Copy
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-full" />
          <blockquote className="pl-4 text-sm sm:text-base text-foreground leading-relaxed italic">
            &ldquo;{decodeHtmlEntities(suggestion)}&rdquo;
          </blockquote>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          This is a suggested opening to boost engagement. Feel free to adapt it to your voice.
        </p>
      </CardContent>
    </Card>
  );
};

export default RewriteSuggestion;
